import { useLocale, useT } from '../i18n/locale-context';
import type { RequirementsState, TenderRequirements } from '../lib/catalog-client';
import { formatBidSecurity } from '../lib/format';

/** Requirement bullet-list fields (qualifications / technical specs) and the
 *  labelled-row fields (delivery / warranty / payment). `scope` is deliberately
 *  NOT rendered here — it duplicates the «Описание» prose above it.
 *
 *  ⚠️ `qualifications` is rendered SEPARATELY (below), not through this list:
 *  it is the one field that carries a provenance footer and an honest empty
 *  state. `technicalSpecs` keeps the plain bullet rendering it always had. */
const LIST_FIELDS = ['technicalSpecs'] as const;
const ROW_FIELDS = ['delivery', 'warranty', 'payment'] as const;

/** The states that have no criteria to show, each with its own explanation.
 *  Keys are the i18n suffix; see `RequirementsState` on the API for what each
 *  one means and how many live lots were in it on 2026-09-25. */
const EMPTY_STATE_COPY: Record<
  Exclude<RequirementsState, 'documents' | 'marketplace'>,
  'noneStated' | 'noDocuments' | 'unreadable' | 'pending'
> = {
  none_stated: 'noneStated',
  no_documents: 'noDocuments',
  unreadable: 'unreadable',
  pending: 'pending',
};

/** Splits the notice around its link word. A control character, so no
 *  translation can ever contain it by accident. */
const ORIGINAL_MARK = '\u0000';

/**
 * «Распознано со скана документа. … сверяйте с оригиналом.» — the caution a
 * lot carries when its document text was transcribed from a scan by the model
 * (API `textFromScan`). In the 2026-10-15 trial upright scans read accurately,
 * but 2 of 20 sideways ones changed a region name, an email and table rows, so
 * the reader is told which facts to check against the file. Same muted line as
 * the requirements provenance footer; «оригиналом» links to the documents
 * section when the page has one.
 */
function ScanNotice({ href }: { href?: string | null }) {
  const t = useT();
  const [before, after = ''] = t('tender.scanNotice', { original: ORIGINAL_MARK }).split(
    ORIGINAL_MARK,
  );
  const word = t('tender.scanNoticeOriginal');
  return (
    <p data-testid="scan-notice" className="mt-2 text-[12px] text-ink-muted">
      {before}
      {href ? (
        <a
          href={href}
          className="font-semibold text-brand underline decoration-brand/30 underline-offset-2 transition-colors hover:decoration-brand"
        >
          {word}
        </a>
      ) : (
        word
      )}
      {after}
    </p>
  );
}

function hasRequirementBody(r: TenderRequirements | null | undefined): boolean {
  if (!r) return false;
  return (
    (r.qualifications?.length ?? 0) > 0 ||
    (r.technicalSpecs?.length ?? 0) > 0 ||
    !!r.delivery ||
    !!r.warranty ||
    !!r.payment
  );
}

/**
 * The tender's whole "understanding" in ONE free card: «Описание» (the prose
 * overview — the generated разбор when present, else the deterministic facts
 * paragraph), «Спецификация» (the source line-items), and «Требования»
 * (qualifications / technical specs / delivery / warranty / payment). All FREE —
 * the paywall is on acting (contacts / documents / analytics), not on reading a
 * tender. No "AI" labelling anywhere; no teaser/fade. Renders nothing when the
 * tender carries none of the three (so we never paint an empty box).
 */
export function TenderOverview({
  description,
  specification,
  specIsSourceFallback,
  requirements,
  requirementsIsSourceFallback,
  requirementsState,
  requirementsDisqualifying,
  requirementsSourceName,
  sourceLocked = false,
  sourceHref,
  documentHref,
  bidSecurityUzs,
  bidSecurityPct,
  budgetUzs,
  textFromScan = false,
  originalHref,
}: {
  /** Prose overview: the summary when present, else the deterministic facts. */
  description: string | null;
  /** Source line-items / spec text. */
  specification: string | null;
  /** True when `specification` fell back to the untranslated source language. */
  specIsSourceFallback?: boolean;
  requirements?: TenderRequirements | null;
  /** True when the API served (part of) `requirements` in the source language
   *  rather than the page's — puts «manba tilida» on the block. Absent on an
   *  API older than 2026-10-20: the note then shows on every uz page, as it
   *  did before translations existed. */
  requirementsIsSourceFallback?: boolean;
  /** WHY the qualification list shows what it shows. Absent on an API build
   *  older than 2026-09-25 — the block then renders exactly as it always did
   *  (list or nothing), never an explanation we cannot stand behind. */
  requirementsState?: RequirementsState | null;
  /** Criteria the SOURCE marks as disqualifying — matched by exact text against
   *  `requirements.qualifications`. Marketplace lists only. */
  requirementsDisqualifying?: string[] | null;
  /**
   * Human name of the originating marketplace, for the provenance copy — or
   * null. ⚠️ Source identity is Pro (2026-10-05): the API sends it only to a
   * viewer who may see the source, and the CALLER gates it on `unlocked` as
   * well, so a web build ahead of the API never prints it to anyone else. With
   * no name the copy says «площадка-источник» instead of going silent.
   */
  requirementsSourceName?: string | null;
  /**
   * The viewer may NOT see the source (`!unlocked`). Picks the `no_documents`
   * wording that says the lot opens in Pro, instead of one that points at a
   * platform the page will not name. Absent = false: a caller that does not
   * know gets the neutral wording, never a Pro pitch to a Pro viewer.
   */
  sourceLocked?: boolean;
  /**
   * Link to the lot on its source portal, for the `no_documents` copy — or null.
   * ⚠️ The CALLER applies the Pro gate (`unlocked && sourceUrl`); this component
   * renders whatever it is handed. The wall on the source triple is unchanged:
   * a non-entitled viewer gets the sentence without the link, which is still
   * strictly more honest than the empty block it replaces.
   */
  sourceHref?: string | null;
  /** Link to the first attachment, for the `unreadable` copy — or null. Gated by
   *  the caller in exactly the same way (`data.documents` is Pro-only). */
  documentHref?: string | null;
  /** Bid security (залог) as stored — EITHER an absolute so'm amount OR a
   *  percent of the budget, depending on the source. See `formatBidSecurity`.
   *  ~14% fill — rendered only when present, no empty label. */
  bidSecurityUzs?: string | null;
  /** The RATE the source published (API `bidSecurityPct`). Since 2026-09-29
   *  `bidSecurityUzs` holds the so'm that rate works out to, so without this
   *  the row would print our arithmetic as a published sum. */
  bidSecurityPct?: number | null;
  /** The lot's budget, needed to turn a percent rate into the «≈ N сум» a
   *  buyer can actually act on. Optional: a rate with no budget prints bare. */
  budgetUzs?: string | null;
  /** The lot's document text was transcribed from a scan (API `textFromScan`).
   *  Puts «Распознано со скана…» under the description and under the first
   *  requirements section built from our own extraction. */
  textFromScan?: boolean;
  /** In-page link to the documents section («#sec-documents») for the notice's
   *  «оригиналом», or null when the page renders no such section. */
  originalHref?: string | null;
}) {
  const t = useT();
  const { locale } = useLocale();
  const desc = description?.trim() || null;
  const spec = specification?.trim() || null;
  const req = hasRequirementBody(requirements) ? requirements! : null;
  const quals = requirements?.qualifications ?? [];
  const disqualifying = new Set(requirementsDisqualifying ?? []);
  // The provenance footer only ever names a source we actually used. It is
  // driven by the STATE, not by "is there a list". A marketplace list whose
  // platform the viewer may not see (no name — see the prop) says «по данным
  // площадки-источника»: still true about WHERE the criteria came from, and it
  // never prints «по данным площадки undefined» or claims they came from the
  // lot's documents.
  const sourceName = requirementsSourceName?.trim() || null;
  const provenance =
    requirementsState === 'marketplace'
      ? sourceName
        ? t('requirements.sourceMarketplace', { name: sourceName })
        : t('requirements.sourceMarketplaceGeneric')
      : requirementsState === 'documents'
        ? t('requirements.sourceDocuments')
        : null;
  // The honest empty state. Only when we have BOTH a state from the API and no
  // criteria to show — a lot that has a list needs no explanation, and a build
  // with no state must keep rendering nothing (that is the old behaviour, and
  // the alternative is inventing a reason).
  const emptyState =
    quals.length === 0 && requirementsState && requirementsState in EMPTY_STATE_COPY
      ? EMPTY_STATE_COPY[requirementsState as keyof typeof EMPTY_STATE_COPY]
      : null;
  // «300 тыс. сум» or «3 % ≈ 8,1 млн сум» — the column holds both units.
  // The ONLY surface that spells a rate out in so'm («3 % ≈ 8,1 млн сум»):
  // there is room for it here, and this is the page where someone decides
  // whether they can afford to bid. Both card surfaces print the bare «3 %».
  const bidSecurity = formatBidSecurity(
    bidSecurityUzs,
    budgetUzs,
    locale,
    t('tender.budget.unit'),
    { compact: true, approxAmount: true, publishedPct: bidSecurityPct },
  );
  // `emptyState` joins the guard: on a lot with no description and no spec the
  // explanation is the only thing worth painting, and it is exactly the case
  // the whole change exists for.
  if (!desc && !spec && !req && !emptyState) return null;

  /*
   * «(manba tilida)» on the REQUIREMENTS sections — only when the API says the
   * block is NOT in the page's language.
   *
   * Until 2026-10-20 `tenders.requirements` was the only copy and every value in
   * it was Russian (verified against prod 2026-09-26 on
   * `cmuia3ev3000dqx0njl0q00ov`: byte-identical for lang=ru and lang=uz), so
   * this note showed on EVERY uz page with requirements — 3 419 of 3 777 live
   * lots that day read as Russian. Since 2026-10-20 the API serves
   * `requirements_uz` — the same object in the deployment's second language
   * (Uzbek here, Kazakh on KZ) — and the marketplace criteria list translated
   * from `evaluation_i18n`, and sets `requirementsIsSourceFallback` exactly when
   * it could not: a lot not translated yet, or a marketplace name the backfill
   * has not reached. That flag decides; nothing here guesses a language from
   * the text (an `ы` is not proof of Russian — see docs/explanation/data-gotchas).
   *
   * Absent flag = an API older than the translations, which only ever served
   * Russian: keep the old rule, mark every uz page. Never on ru — the API never
   * flags the default locale, which owns the Russian wording; a note there would
   * fire on every lot. The note says «in the source language», which is true
   * whatever that language is.
   *
   * ONE marker per card, on the FIRST section that actually renders: the API
   * answers for the block with one flag, and a second and third copy would read
   * as three separate facts about three separate provenances.
   */
  const inSourceLanguage = requirementsIsSourceFallback ?? locale === 'uz';
  const sourceLangMark = inSourceLanguage ? (
    <span className="ml-2 font-medium normal-case tracking-normal text-ink-muted/80">
      ({t('tender.sourceLangNote')})
    </span>
  ) : null;
  const hasRows = Boolean(req && (req.delivery || req.warranty || req.payment));
  /** Which requirements section carries the marker — the first one with content.
   *  `null` when nothing un-localised renders (an empty state is OUR copy, in the
   *  reader's own language, so marking it would be false). */
  const markedSection: 'qualifications' | 'technicalSpecs' | 'rows' | null = !sourceLangMark
    ? null
    : quals.length > 0
      ? 'qualifications'
      : (req?.technicalSpecs?.length ?? 0) > 0
        ? 'technicalSpecs'
        : hasRows
          ? 'rows'
          : null;

  /*
   * Where the scan notice goes in the requirements part: ONE copy, on the first
   * section that came from OUR extraction of the scanned text. A marketplace
   * criteria list is the platform's own structured data, not the scan — so it
   * is skipped, and the notice moves to the technical specs or the rows, which
   * still come from the documents.
   */
  const scanSection: 'qualifications' | 'technicalSpecs' | 'rows' | null = !textFromScan
    ? null
    : quals.length > 0 && requirementsState !== 'marketplace'
      ? 'qualifications'
      : (req?.technicalSpecs?.length ?? 0) > 0
        ? 'technicalSpecs'
        : hasRows
          ? 'rows'
          : null;
  const scanNotice = <ScanNotice href={originalHref} />;

  // These are the tender's real content sections (Описание / Спецификация /
  // Требования), so they are h2 — not styled divs. Previously the ONLY h2 on a
  // tender page was the paywall notice ("Документы и контакты заказчика скрыты"),
  // which meant the single subheading Google saw on ~13k URLs was an upsell.
  // Tailwind preflight resets heading margins/size, so the tag change is
  // visually identical.
  const label = (text: string, mark?: boolean) => (
    <h2 className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted">
      {text}
      {mark && sourceLangMark}
    </h2>
  );

  return (
    <section
      id="sec-summary"
      className="scroll-mt-16 rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6"
    >
      {desc && (
        <div className="mb-5 last:mb-0">
          {label(t('tender.overview.description'))}
          <p className="whitespace-pre-line text-[14px] leading-relaxed text-ink-soft">{desc}</p>
          {textFromScan && scanNotice}
        </div>
      )}

      {spec && (
        <div className="mb-5 last:mb-0">
          <h2 className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted">
            {t('tender.overview.specification')}
            {specIsSourceFallback && (
              <span className="ml-2 font-medium normal-case tracking-normal text-ink-muted/80">
                ({t('tender.sourceLangNote')})
              </span>
            )}
          </h2>
          <p className="whitespace-pre-line text-[14px] leading-relaxed text-ink-soft">{spec}</p>
        </div>
      )}

      {/* «Квалификационные требования» — the bidder's own checklist, and the one
          section that says WHERE it came from and, when it is empty, WHY. Before
          2026-09-25 it simply vanished on 69% of the live catalogue, so a gap in
          our pipeline and a gap in the lot looked identical. */}
      {(quals.length > 0 || emptyState) && (
        <div className="mb-5 last:mb-0">
          {label(t('requirements.qualifications'), markedSection === 'qualifications')}
          {quals.length > 0 ? (
            <ul className="flex flex-col gap-1.5">
              {quals.map((item, i) => (
                <li key={i} className="flex gap-2 text-[14px] leading-relaxed text-ink-soft">
                  <span aria-hidden className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-brand" />
                  <span>
                    {item}
                    {/* The source's own «несоответствие = отклонение» flag. Same
                        criteria the integrity panel counts, so a reader who sees
                        «Дисквалифицирующих требований: 2» there can find WHICH
                        two here. */}
                    {disqualifying.has(item) && (
                      <span className="ml-1.5 whitespace-nowrap rounded bg-bone px-1.5 py-0.5 text-[11px] font-semibold text-ink-muted">
                        {t('requirements.disqualifying')}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[14px] leading-relaxed text-ink-soft">
              {/* no_documents: the named sentence for a viewer who may see the
                  source (today's wording, with the «Открыть лот на площадке» link
                  below); for everyone else the platform stays unnamed and the
                  sentence says where opening the lot lives — in Pro. */}
              {emptyState === 'noDocuments'
                ? sourceName
                  ? t('requirements.noDocuments', { name: sourceName })
                  : t(
                      sourceLocked
                        ? 'requirements.noDocumentsLocked'
                        : 'requirements.noDocumentsGeneric',
                    )
                : t(`requirements.${emptyState}`)}
              {emptyState === 'noDocuments' && sourceHref && (
                <>
                  {' '}
                  <a
                    href={sourceHref}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="font-semibold text-brand underline decoration-brand/30 underline-offset-2 transition-colors hover:decoration-brand"
                  >
                    {t('requirements.openOnSource')}
                  </a>
                </>
              )}
              {emptyState === 'unreadable' && documentHref && (
                <>
                  {' '}
                  <a
                    href={documentHref}
                    target="_blank"
                    // nofollow for the same reason the documents card uses it:
                    // the download proxy is not a page, and Googlebot followed
                    // those links into /api/documents/download (GSC, 2026-09-07).
                    rel="noopener noreferrer nofollow"
                    className="font-semibold text-brand underline decoration-brand/30 underline-offset-2 transition-colors hover:decoration-brand"
                  >
                    {t('requirements.openDocument')}
                  </a>
                </>
              )}
            </p>
          )}
          {provenance && <p className="mt-2 text-[12px] text-ink-muted">{provenance}</p>}
          {scanSection === 'qualifications' && scanNotice}
        </div>
      )}

      {req &&
        LIST_FIELDS.map((field) => {
          const items = req[field];
          if (!items || items.length === 0) return null;
          return (
            <div key={field} className="mb-5 last:mb-0">
              {label(t(`requirements.${field}`), markedSection === 'technicalSpecs')}
              <ul className="flex flex-col gap-1.5">
                {items.map((item, i) => (
                  <li key={i} className="flex gap-2 text-[14px] leading-relaxed text-ink-soft">
                    <span aria-hidden className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-brand" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              {scanSection === 'technicalSpecs' && scanNotice}
            </div>
          );
        })}

      {bidSecurity && (
        <dl className="border-t border-edge-light pt-3 text-[14px]">
          <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
            <dt className="shrink-0 text-ink-muted sm:w-32">{t('tender.overview.bidSecurity')}</dt>
            <dd className="font-medium text-ink">{bidSecurity}</dd>
          </div>
        </dl>
      )}

      {hasRows && (
        <div className="border-t border-edge-light pt-3">
          {/* These rows have no heading of their own, so when they are the first
              un-localised thing on the card the marker goes above them. */}
          {markedSection === 'rows' && (
            <p className="mb-1.5 text-[11px] font-medium text-ink-muted/80">
              ({t('tender.sourceLangNote')})
            </p>
          )}
          <dl className="flex flex-col gap-1.5 text-[14px]">
            {ROW_FIELDS.map((field) => {
              const value = req?.[field];
              if (!value) return null;
              return (
                <div key={field} className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
                  <dt className="shrink-0 text-ink-muted sm:w-32">{t(`requirements.${field}`)}</dt>
                  <dd className="font-medium text-ink">{value}</dd>
                </div>
              );
            })}
          </dl>
          {scanSection === 'rows' && scanNotice}
        </div>
      )}
    </section>
  );
}
